/**
 * Auth du Portail Corporate (Novelis & équivalents) — Phase 101cx-corp.
 *
 * Miroir de shopAuth.js mais pour la collection `corporate_contacts`.
 * Cookies dédiés : corporate_access_token / corporate_refresh_token.
 * Endpoints : /api/corporate/auth/*
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const CORP_API = `${BACKEND_URL}/api/corporate`;

export const corporateApi = axios.create({
  baseURL: CORP_API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

const CorporateAuthContext = createContext(null);

export function CorporateAuthProvider({ children }) {
  // null = checking · false = anonymous · object = authenticated
  const [contact, setContact] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await corporateApi.get("/auth/me");
      setContact(data);
    } catch {
      setContact(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await corporateApi.post("/auth/login", { email, password });
    setContact(data);
    return data;
  }, []);

  const activate = useCallback(async (token, password) => {
    const { data } = await corporateApi.post("/auth/activate", { token, password });
    setContact(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await corporateApi.post("/auth/logout");
    } catch {
      // ignore
    }
    setContact(false);
  }, []);

  return (
    <CorporateAuthContext.Provider value={{ contact, refresh, login, activate, logout }}>
      {children}
    </CorporateAuthContext.Provider>
  );
}

export function useCorporateAuth() {
  const ctx = useContext(CorporateAuthContext);
  if (!ctx) throw new Error("useCorporateAuth must be used inside CorporateAuthProvider");
  return ctx;
}

export function formatCorpErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
